import React from "react";

import { color } from "minimum-ui/tokens";
import styled, { css, keyframes } from "styled-components";
import { Table } from "ui/core";

export interface SkeletonProps {
  config: number[];
  expand?: boolean;
  height?: number;
}

/**
 * Display a skeleton loader row. Configuration accepts an array representing
 * columns, each number being how many skeletons to show in that cell
 */
const Skeleton: React.FC<SkeletonProps> = ({ config, expand, height }) => {
  const content = config.map((count, index) => {
    const iterator = Array.from(new Array(count));
    return (
      <Cell key={index} $right={index === config.length - 1} $height={height}>
        {iterator.map((_value, index) => (
          <Loader key={index} />
        ))}
      </Cell>
    );
  });

  if (expand) {
    return <Table.ExpandableRow columns={[1]} contents={content} />;
  }

  return <>{content}</>;
};

const colorFade = keyframes`
  0% {
    background-color: ${color.neutral[200]};
  }

  50% {
    background-color: ${color.neutral[100]};
  }

  100% {
    background-color: ${color.neutral[200]};
  }
`;
const Loader = styled.span`
  display: inline-block;
  background-color: ${color.neutral[200]};
  height: 1.25rem;
  border-radius: 10px;
  animation: 1.5s ${colorFade} infinite;
  width: 24px;

  // Single item
  &:first-child:last-child {
    width: 100%;
    max-width: 250px;
    border-radius: 4px;
  }
`;
const Cell = styled(Table.Cell)<{ $right: boolean; $height?: number }>`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  min-height: ${({ $height }) => ($height ? `${$height}px` : "0px")};

  ${(props) =>
    !props.$right &&
    css`
      padding-right: 45%;
    `}

  ${(props) =>
    props.$right &&
    css`
      padding-left: 45%;
      justify-content: flex-end;
    `}
`;

export default Skeleton;
